import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Box, Paper, Typography, Button, Chip, Alert, Fade, LinearProgress,
  Collapse, Tabs, Tab, CircularProgress, Stack,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import RemoveOutlinedIcon from "@mui/icons-material/RemoveOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  analizarNorma, getCupoAnalizador, getHistorialAnalizador, getDetalleAnalizador,
} from "../../componentes/http/actions/DocumentoAction";

// Paleta del impacto: fondo tenue, acento medio, texto oscuro (de la misma familia)
const IMPACTO = {
  verde:    { fill: "#639922", tint: "#EAF3DE", text: "#27500A", label: "Impacto bajo",  Icono: CheckCircleOutlineIcon },
  amarillo: { fill: "#EF9F27", tint: "#FAEEDA", text: "#633806", label: "Impacto medio", Icono: WarningAmberRoundedIcon },
  rojo:     { fill: "#E24B4A", tint: "#FCEBEB", text: "#791F1F", label: "Impacto alto",  Icono: ErrorOutlineIcon },
};

// Color de la barrita lateral de cada tipo de ítem
const BARRA = { obligacion: "#F09595", compromiso: "#FAC775" };

const normalizarAnalisis = (data) => {
  const imp = data.impacto ?? data.Impacto ?? {};
  return {
    resumen: data.resumen ?? data.Resumen ?? "",
    impacto: {
      color: (imp.color ?? imp.Color ?? "verde").toLowerCase(),
      nivel: imp.nivel ?? imp.Nivel ?? "Bajo",
      puntajeTotal: imp.puntajeTotal ?? imp.PuntajeTotal ?? 0,
      parametros: (imp.parametros ?? imp.Parametros ?? []).map((p) => ({
        etiqueta: p.etiqueta ?? p.Etiqueta ?? "",
        puntaje: p.puntaje ?? p.Puntaje ?? 0,
        justificacion: p.justificacion ?? p.Justificacion ?? "",
      })),
    },
    obligaciones: (data.obligaciones ?? data.Obligaciones ?? [])
      .map((o) => o.Obligacion ?? o.obligacion ?? "").filter(Boolean),
    compromisos: (data.compromisos ?? data.Compromisos ?? [])
      .map((c) => c.Compromiso ?? c.compromiso ?? "").filter(Boolean),
  };
};

const AnalizadorNorma = () => {
  const token = localStorage.getItem("token");
  const inputRef = useRef(null);

  const [tab, setTab] = useState(0);
  const [archivo, setArchivo] = useState(null);
  const [estado, setEstado] = useState("inicio");
  const [resultado, setResultado] = useState(null);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [verCriterios, setVerCriterios] = useState(false);
  const [cupo, setCupo] = useState(null);

  const cargarCupo = useCallback(async () => {
    try { setCupo(await getCupoAnalizador(token)); } catch { /* silencioso */ }
  }, [token]);
  useEffect(() => { cargarCupo(); }, [cargarCupo]);

  const reiniciar = () => {
    setArchivo(null); setEstado("inicio"); setResultado(null);
    setError(null); setVerCriterios(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const procesar = useCallback(async (file) => {
    if (!file) return;
    const esPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!esPdf) { setError("El archivo debe ser un PDF."); setEstado("error"); return; }

    setArchivo(file); setError(null); setResultado(null); setEstado("analizando");
    try {
      const data = await analizarNorma(token, file);
      const norm = normalizarAnalisis(data.analisis ?? data.Analisis ?? {});
      if (!norm.resumen && norm.obligaciones.length === 0 && norm.compromisos.length === 0)
        throw new Error("No se obtuvo contenido analizable del documento.");
      setResultado(norm);
      setCupo(data.cupo ?? data.Cupo ?? cupo);
      setEstado("listo");
    } catch (e) {
      setError(e?.response?.data?.mensaje || e?.message || "No se pudo completar el análisis.");
      setEstado("error");
    }
  }, [token, cupo]);

  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); procesar(e.dataTransfer.files?.[0]); };

  const verDesdeHistorial = async (id) => {
    setTab(0); setEstado("analizando"); setArchivo(null); setVerCriterios(false);
    try {
      const detalle = await getDetalleAnalizador(token, id);
      setArchivo({ name: detalle.nombreArchivo ?? detalle.NombreArchivo });
      setResultado(normalizarAnalisis(detalle.analisis ?? detalle.Analisis ?? {}));
      setEstado("listo");
    } catch {
      setError("No se pudo abrir el análisis."); setEstado("error");
    }
  };

  return (
    <Box sx={{ maxWidth: 780, mx: "auto", p: { xs: 2, sm: 3 } }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Analizador de normas</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Sube un documento normativo en PDF para obtener su nivel de impacto, un resumen
        ejecutivo y el detalle de obligaciones y compromisos.
      </Typography>

      {cupo && cupo.activo !== false && (cupo.limite ?? cupo.Limite) > 0 && (
        <Chip
          size="small"
          variant="outlined"
          sx={{ mb: 2 }}
          label={`Cupo semanal: ${cupo.usados ?? cupo.Usados ?? 0} de ${cupo.limite ?? cupo.Limite} usados`}
          color={(cupo.restantes ?? cupo.Restantes ?? 1) <= 0 ? "error" : "default"}
        />
      )}

      <Tabs value={tab} onChange={(e, v) => setTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider", minHeight: 42 }}>
        <Tab icon={<AutoAwesomeOutlinedIcon sx={{ fontSize: 18 }} />} iconPosition="start"
          label="Analizar" sx={{ minHeight: 42, textTransform: "none" }} />
        <Tab icon={<HistoryOutlinedIcon sx={{ fontSize: 18 }} />} iconPosition="start"
          label="Historial" sx={{ minHeight: 42, textTransform: "none" }} />
      </Tabs>

      {tab === 0 && (
        <>
          {estado === "inicio" && (
            <Paper
              variant="outlined"
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              sx={{
                p: 6, textAlign: "center", cursor: "pointer",
                borderStyle: "dashed", borderWidth: 2, borderRadius: 3,
                borderColor: dragOver ? "primary.main" : "divider",
                bgcolor: dragOver ? "action.hover" : "transparent",
                transition: "all .15s ease",
              }}
            >
              <input ref={inputRef} type="file" accept=".pdf,application/pdf" hidden
                onChange={(e) => procesar(e.target.files?.[0])} />
              <CloudUploadOutlinedIcon sx={{ fontSize: 44, color: "primary.main", mb: 1.5 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Arrastra un PDF aquí o haz clic para seleccionar
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                El análisis comienza al cargar el archivo
              </Typography>
            </Paper>
          )}

          {estado === "analizando" && (
            <Box sx={{ py: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{archivo?.name || "Cargando…"}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Extrayendo texto y evaluando el documento…
              </Typography>
              <LinearProgress />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                El análisis con IA puede tardar hasta un minuto según el largo del documento.
              </Typography>
            </Box>
          )}

          {estado === "error" && (
            <Box>
              <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
              <Button startIcon={<RefreshOutlinedIcon />} onClick={reiniciar} sx={{ textTransform: "none" }}>
                Volver a intentar
              </Button>
            </Box>
          )}

          {estado === "listo" && resultado && (
            <Fade in>
              <Box>
                {/* Barra superior: archivo + acción */}
                <Box sx={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  pb: 1.5, mb: 2.5, borderBottom: 1, borderColor: "divider", gap: 2,
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                    <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: "text.secondary", flexShrink: 0 }} />
                    <Typography variant="body2" color="text.secondary" noWrap>{archivo?.name}</Typography>
                  </Box>
                  <Button size="small" onClick={reiniciar} sx={{ textTransform: "none", flexShrink: 0 }}>
                    Analizar otra norma
                  </Button>
                </Box>

                <Impacto impacto={resultado.impacto} abierto={verCriterios} onToggle={() => setVerCriterios((v) => !v)} />

                <Seccion icon={<NotesOutlinedIcon sx={{ fontSize: 18 }} />} iconColor="primary.main" titulo="Resumen">
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    {resultado.resumen || "El documento no arrojó un resumen."}
                  </Typography>
                </Seccion>

                <Seccion icon={<GavelOutlinedIcon sx={{ fontSize: 18 }} />} iconColor="#A32D2D"
                  titulo="Obligaciones" count={resultado.obligaciones.length}
                  countBg="#FCEBEB" countColor="#791F1F">
                  {resultado.obligaciones.length === 0
                    ? <Vacio texto="No se identificaron obligaciones en el documento." />
                    : <ListaItems items={resultado.obligaciones} barra={BARRA.obligacion} />}
                </Seccion>

                <Seccion icon={<TaskAltOutlinedIcon sx={{ fontSize: 18 }} />} iconColor="#854F0B"
                  titulo="Compromisos" count={resultado.compromisos.length}
                  countBg="#FAEEDA" countColor="#633806">
                  {resultado.compromisos.length === 0
                    ? <Vacio texto="No se identificaron compromisos en el documento." />
                    : <ListaItems items={resultado.compromisos} barra={BARRA.compromiso} />}
                </Seccion>

                <Typography variant="caption" color="text.secondary"
                  sx={{ display: "block", mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
                  Resultado generado por IA a partir del texto del documento. Revisa el contenido
                  antes de tomar decisiones legales.
                </Typography>
              </Box>
            </Fade>
          )}
        </>
      )}

      {tab === 1 && <Historial token={token} onVer={verDesdeHistorial} />}
    </Box>
  );
};

// ── Impacto: tarjeta con fondo tenue + icono redondo + barra de 5 segmentos ─
const Impacto = ({ impacto, abierto, onToggle }) => {
  const cfg = IMPACTO[impacto.color] || IMPACTO.verde;
  const Icono = cfg.Icono;
  return (
    <Paper variant="outlined" sx={{ mb: 3.5, borderRadius: 3, overflow: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, px: 2.25, py: 2, bgcolor: cfg.tint }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: "50%", bgcolor: cfg.fill,
          display: "grid", placeItems: "center", flexShrink: 0,
        }}>
          <Icono sx={{ fontSize: 20, color: cfg.text }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: cfg.text, lineHeight: 1.2 }}>
            {cfg.label}
          </Typography>
          <Typography sx={{ fontSize: 13, color: cfg.text, opacity: 0.85, mt: 0.25 }}>
            {impacto.puntajeTotal} de 5 criterios de criticidad
          </Typography>
        </Box>
        <Button size="small" onClick={onToggle}
          endIcon={abierto ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{ color: cfg.text, textTransform: "none", flexShrink: 0 }}>
          Ver criterios
        </Button>
      </Box>

      {/* Barra de 5 segmentos */}
      <Stack direction="row" spacing={0.5} sx={{ px: 2.25, py: 1.25 }}>
        {[0, 1, 2, 3, 4].map((i) => (
          <Box key={i} sx={{
            flex: 1, height: 4, borderRadius: 1,
            bgcolor: i < impacto.puntajeTotal ? cfg.fill : "divider",
          }} />
        ))}
      </Stack>

      <Collapse in={abierto} timeout="auto" unmountOnExit>
        <Box sx={{ px: 2.25, pb: 2, pt: 0.5 }}>
          {impacto.parametros.map((p, i) => {
            const activo = p.puntaje >= 1;
            return (
              <Box key={i} sx={{
                display: "flex", gap: 1.25, py: 1.25,
                borderTop: 1, borderColor: "divider",
                opacity: activo ? 1 : 0.6,
              }}>
                <Box sx={{ mt: 0.25, flexShrink: 0 }}>
                  {activo
                    ? <CheckOutlinedIcon sx={{ fontSize: 18, color: cfg.fill }} />
                    : <RemoveOutlinedIcon sx={{ fontSize: 18, color: "text.disabled" }} />}
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.etiqueta}</Typography>
                  {p.justificacion && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, lineHeight: 1.55 }}>
                      {p.justificacion}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Paper>
  );
};

// ── Sección: icono a color + título + pastilla de conteo ───────────────────
const Seccion = ({ icon, iconColor, titulo, count, countBg, countColor, children }) => (
  <Box sx={{ mb: 3.5 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
      <Box sx={{ color: iconColor, display: "flex" }}>{icon}</Box>
      <Typography sx={{ fontSize: 16, fontWeight: 600 }}>{titulo}</Typography>
      {typeof count === "number" && (
        <Box sx={{
          fontSize: 12, fontWeight: 600, px: 1, py: 0.15, borderRadius: 5,
          bgcolor: countBg || "action.hover", color: countColor || "text.secondary",
        }}>
          {count}
        </Box>
      )}
    </Box>
    {children}
  </Box>
);

// ── Ítems: tarjeta con barrita vertical de color a la izquierda ────────────
const ListaItems = ({ items, barra }) => (
  <Stack spacing={1.25}>
    {items.map((texto, i) => (
      <Paper key={i} variant="outlined" sx={{ display: "flex", gap: 1.5, p: 2, borderRadius: 2.5 }}>
        <Box sx={{ width: 6, alignSelf: "stretch", borderRadius: 3, bgcolor: barra, flexShrink: 0 }} />
        <Typography variant="body1" sx={{ lineHeight: 1.6 }}>{texto}</Typography>
      </Paper>
    ))}
  </Stack>
);

const Vacio = ({ texto }) => (
  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>{texto}</Typography>
);

// ── Historial ──────────────────────────────────────────────────────────────
const Historial = ({ token, onVer }) => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    getHistorialAnalizador(token).then(setItems).catch(() => setItems([]));
  }, [token]);

  if (items === null)
    return <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress size={28} /></Box>;

  if (items.length === 0)
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <Typography variant="body1" color="text.secondary">
          Aún no has analizado ninguna norma. Sube un PDF en la pestaña Analizar.
        </Typography>
      </Box>
    );

  return (
    <Box>
      {items.map((it, i) => {
        const color = (it.colorSemaforo ?? it.ColorSemaforo ?? "verde").toLowerCase();
        const cfg = IMPACTO[color] || IMPACTO.verde;
        const fecha = new Date(it.fechaAnalisis ?? it.FechaAnalisis);
        return (
          <Box key={it.id ?? it.Id} sx={{
            display: "flex", alignItems: "center", gap: 1.5, py: 1.75,
            borderTop: i === 0 ? 1 : 0, borderBottom: 1, borderColor: "divider",
          }}>
            <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: cfg.fill, flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                {it.nombreArchivo ?? it.NombreArchivo}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {fecha.toLocaleDateString("es-CL")} · {it.cantidadObligaciones ?? it.CantidadObligaciones ?? 0} obligaciones · {it.cantidadCompromisos ?? it.CantidadCompromisos ?? 0} compromisos
              </Typography>
            </Box>
            <Button size="small" onClick={() => onVer(it.id ?? it.Id)} sx={{ textTransform: "none", flexShrink: 0 }}>
              Ver
            </Button>
          </Box>
        );
      })}
    </Box>
  );
};

export default AnalizadorNorma;